/**
 * A concept's title, description and classification — ROUTE_INVENTORY §2.10 #41.
 *
 * It is the only part of the panel that derives anything: `mastery` is
 * computed from three FSRS fields and `importanceTier` buckets a continuous
 * score, so what the user reads here is never what the API sent. That
 * derivation is the reason it is worth a story — every other block renders
 * what it is handed.
 *
 * ## Description first, and every classification labelled
 *
 * The strip used to sit directly under the title as three bare chips:
 * `Core` · `★ Core concept` · `● Learning`. Three different axes — how deep
 * the material goes, how central the concept is, how well you have learned it
 * — rendered in near-identical brand chips with nothing saying which was
 * which, and the first two both reading "Core". A reader had no way to decode
 * it, and it pushed the one thing they actually came for, the description,
 * below the fold of a 420px panel.
 *
 * So the description comes first, and the classifications are a two-column
 * list with their names beside them. The pills keep their contrast-tuned
 * styling; they are just no longer expected to explain themselves.
 *
 * Two things moved out. **Mastery**, into the memory section: "Mastery:
 * Learning" sitting a few rows above "Memory: Strong memory" reads as two
 * answers to one question. And the **title**, into the panel header, where it
 * sits on the same row as the close button and stays put while the body
 * scrolls.
 *
 * What is left describes the CONCEPT — what it is, how advanced the material
 * is, how central it is — and how you are doing with it is answered in one
 * place, below.
 *
 * `Level` is the API's own word (`foundational | core | advanced`) and the
 * backend ranks them in that order, so it is a statement about the material's
 * difficulty. `Importance` is about how central the concept is to the lessons
 * that taught it. Two different questions that both happen to have an answer
 * called "Core", which is exactly why neither is allowed to appear unlabelled.
 */

import { VisualBlock } from '@/components/core/visual';
import { BlockMarkdown } from '@/components/core/markdown/markdown';
import type { ConceptDetail } from '@/types/mindspace-concept';
import { importanceTier } from '@/utils/mindspaceDisplay';
import {
  ImportancePill,
  LevelPill,
} from '@/components/features/mindspace/concepts/ConceptBadges';

export function ConceptHeroBlock({ concept }: { concept: ConceptDetail }) {
  const tier = importanceTier(concept.importance_score);

  return (
    <div>
      {/* Directly under the panel's heading, before the prose: these two
          classify the concept, so they belong with its name rather than
          after a paragraph about it. */}
      <dl className='space-y-1.5'>
        {/* `level` is optional on the API type — older concepts predate it, and
            an absent classification is not the same as "Foundational". */}
        {concept.level && (
          <ClassificationRow label='Level'>
            <LevelPill level={concept.level} />
          </ClassificationRow>
        )}
        <ClassificationRow label='Importance'>
          <ImportancePill tier={tier} />
        </ClassificationRow>
      </dl>

      {concept.visual && (
        <VisualBlock visual={concept.visual} compact className='mt-3' />
      )}

      {concept.description && (
        <BlockMarkdown className='mt-3 text-sm leading-relaxed text-foreground/80'>
          {concept.description}
        </BlockMarkdown>
      )}
    </div>
  );
}

/**
 * One labelled classification. The label is the fix: these three rows are
 * different questions about the same concept, and the values alone cannot
 * tell you which question each is answering.
 */
function ClassificationRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className='flex items-center gap-2'>
      <dt className='w-20 flex-shrink-0 text-xs text-muted-foreground'>
        {label}
      </dt>
      <dd className='min-w-0'>{children}</dd>
    </div>
  );
}
